import {
	enableProdMode,
	provideZoneChangeDetection,
	ErrorHandler,
	APP_INITIALIZER,
	importProvidersFrom,
} from '@angular/core';

import { environment } from '@lmc-env/environment';
import { LicenseManager } from 'ag-grid-enterprise';
import * as Sentry from '@sentry/angular';
import {
	DatePipe,
	CurrencyPipe,
	CommonModule,
	NgOptimizedImage,
} from '@angular/common';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { CustomFunctionService } from './app/custom-function.service';
import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import {
	provideCharts,
	withDefaultRegisterables,
	BaseChartDirective,
} from 'ng2-charts';
import {
	REMOVE_STYLES_ON_COMPONENT_DESTROY,
	BrowserModule,
	bootstrapApplication,
} from '@angular/platform-browser';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { MAT_TABS_CONFIG, MatTabsModule } from '@angular/material/tabs';
import { Router, withRouterConfig, provideRouter } from '@angular/router';
import {
	provideHttpClient,
	withXhr,
	withInterceptorsFromDi,
	HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { CredentialsInterceptor } from '@lmc-app/core/interceptors/credentials.interceptor';
import { AuthErrorInterceptor } from '@lmc-app/core/interceptors/auth-error.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { NgSelectModule } from '@ng-select/ng-select';
import { AgGridModule } from 'ag-grid-angular';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { routes } from './app/app-routing.module';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StoreModule } from '@ngrx/store';
import {
	reducers,
	metaReducers,
	AuthEffects,
	UserEffects,
	PermissionsEffects,
} from './app/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { NgxPaginationModule } from 'ngx-pagination';
import { extModules } from './app/build-specifics';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTreeModule } from '@angular/material/tree';
import { NgxPrintModule } from 'ngx-print';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AppFormlyModule } from '@lmc-app/shared/formly/formly.module';
import { AppComponent } from './app/app.component';

LicenseManager.setLicenseKey(
	'Using_this_AG_Grid_Enterprise_key_( AG-041326 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( LM Credit )_is_granted_a_( Single Application )_Developer_License_for_the_application_( LM Credit )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( LM Credit )_need_to_be_licensed___( LM Credit )_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 20 April 2024 )____[v2]_MTcxMzU2NzYwMDAwMA==5f2a8ea48f043050912892f4313d5b79'
);

if (environment.production) {
	enableProdMode();
	Sentry.init({
		dsn: 'https://9841c3281a4dfe0aade0b34d5f1cecf0@o4510507406786560.ingest.us.sentry.io/4510511242739712',
		// v11 replaced sendDefaultPii; its defaults match `true`, except HTTP bodies, which v10 never sent
		dataCollection: { httpBodies: [] },
		beforeSend(event, hint) {
			const error = hint.originalException;
			if (error && typeof error === 'object') {
				const hasHttpProperties =
					'status' in error ||
					'statusText' in error ||
					('url' in error && 'ok' in error);
				if (hasHttpProperties) {
					return null;
				}
				const errorName = (error as any).name;
				if (errorName === 'HttpErrorResponse') {
					console.log('[Sentry] Ignoring HttpErrorResponse:', error);
					return null;
				}
			}

			return event; // Send other errors to Sentry
		},
	});
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		importProvidersFrom(
			BrowserAnimationsModule,
			NgxExtendedPdfViewerModule,
			NgSelectModule,
			AgGridModule,
			NgbAlertModule,
			MatToolbarModule,
			MatChipsModule,
			MatListModule,
			MatTabsModule,
			MatSlideToggleModule,
			MatCheckboxModule,
			MatDialogModule,
			CommonModule,
			MatInputModule,
			MatNativeDateModule,
			BrowserModule,
			MatCardModule,
			MatFormFieldModule,
			MatSelectModule,
			MatIconModule,
			MatDatepickerModule,
			FormsModule,
			ReactiveFormsModule,
			MatButtonModule,
			MatSortModule,
			BaseChartDirective,
			MatProgressSpinnerModule,
			StoreModule.forRoot(reducers, { metaReducers }),
			StoreDevtoolsModule.instrument({
				maxAge: 25,
				logOnly: false, // environment.production
				autoPause: true,
				trace: false,
				traceLimit: 75,
				connectInZone: true,
			}),
			EffectsModule.forRoot([AuthEffects, UserEffects, PermissionsEffects]),
			NgxPaginationModule,
			extModules,
			MatSidenavModule,
			MatStepperModule,
			MatExpansionModule,
			MatGridListModule,
			MatTreeModule,
			NgxPrintModule,
			MatProgressBarModule,
			MatRadioModule,
			MatSnackBarModule,
			MatBottomSheetModule,
			MatTableModule,
			MatMenuModule,
			MatPaginatorModule,
			MatButtonToggleModule,
			NgxMaskDirective,
			NgxMaskPipe,
			NgxMatSelectSearchModule,
			AngularFireModule.initializeApp({
				apiKey: 'AIzaSyC__iUcS6cPfeUCpILVp5PQTTbOd_hQeZQ',
				authDomain: 'lmcredit-testing.firebaseapp.com',
				projectId: 'lmcredit-testing',
				storageBucket: 'lmcredit-testing.firebasestorage.app',
				messagingSenderId: '708345846541',
				appId: '1:708345846541:web:637466ae2a4dc972acee44',
				measurementId: 'G-H4RHQF32MC',
			}),
			AngularFireMessagingModule,
			NgxSkeletonLoaderModule,
			MatTooltipModule,
			MatDividerModule,
			NgxMatTimepickerModule,
			NgOptimizedImage,
			DragDropModule,
			AppFormlyModule
		),
		DatePipe,
		CurrencyPipe,
		LegacyApiService,
		CustomFunctionService,
		provideNgxMask(),
		provideCharts(withDefaultRegisterables()),
		// Keep component styles after destroy (pre-v17 behaviour); some pages rely on them
		{ provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
		// Keep CDK overlays in the overlay container (pre-v21); Bootstrap modals and ng-select stack by z-index
		{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
		{
			provide: MAT_TABS_CONFIG,
			useValue: { animationDuration: '0ms' }, // Set your desired duration here
		},
		// Sentry
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		provideHttpClient(withXhr(), withInterceptorsFromDi()),
		// HTTP Interceptors
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CredentialsInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthErrorInterceptor,
			multi: true,
		},
		// Angular 22 changed the default to 'always'; keep the previous behaviour
		provideRouter(
			routes,
			withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
		),
	],
}).catch((err) => console.error(err));
